/**
 * Dev Connect: the Friday showcase sign-up sheet.
 *
 * Three or four people demo what they shipped, in a one-hour session, roughly
 * every OTHER Friday. The cadence is not a rule anything here can derive - which
 * Friday is on is a human decision about release weeks and holidays - so a session
 * exists because an admin opened it, and nothing in this file generates one.
 *
 * The helpers below answer "is this date a Friday" and "when does booking shut".
 * They no longer decide *whether* a session happens; a list of the next N Fridays
 * used to be exactly that, and on a fortnightly cadence it manufactured sessions
 * on every off-week.
 *
 * The two are deliberately different kinds of value, and mixing them is the bug
 * this file exists to prevent:
 *
 * - *Which Friday* is a business day - a `PlainDate` in Asia/Kolkata, like every
 *   other window boundary in the system (see date.ts).
 * - *Whether booking is open* is an offset from a specific moment, so it compares
 *   real instants - the same call `oneOnOneState` makes about `scheduledAt`.
 */

import {
  BUSINESS_TIME_ZONE,
  addDays,
  formatPlainDate,
  parsePlainDate,
  startOfDay,
  type PlainDate,
} from './date';

/** Slots on the sheet when nobody has said otherwise. */
export const DEV_CONNECT_DEFAULT_CAPACITY = 4;

/**
 * How many Fridays `upcomingFridays` walks by default.
 *
 * No longer a horizon on anything the user sees: the sheet lists the sessions an
 * admin has opened, with no upper bound, and there is no generator. Kept because
 * `upcomingFridays` still needs a default count.
 */
export const DEV_CONNECT_HORIZON_WEEKS = 4;

/** Booking closes this long before the session starts. */
export const DEV_CONNECT_BOOKING_CUTOFF_HOURS = 24;

/** When a session starts, absent an admin saying otherwise. Wall clock, IST. */
export const DEV_CONNECT_DEFAULT_START_LOCAL = '09:00';

const FRIDAY = 5;

/**
 * The weekday of a PlainDate, 0 = Sunday.
 *
 * `getUTCDay`, never `getDay`. `parsePlainDate` builds UTC midnight, so `getDay`
 * would report the *local* weekday of that instant: west of UTC, 2026-09-11 - a
 * Friday - reads as Thursday, and the whole sheet shifts by a day for anyone whose
 * server is not in Asia/Kolkata. IST being DST-free does not save you here; it is
 * the constant 5h30m offset that does the damage, not a seasonal shift.
 */
export function dayOfWeek(date: PlainDate): number {
  return parsePlainDate(date).getUTCDay();
}

export function isFriday(date: PlainDate): boolean {
  return dayOfWeek(date) === FRIDAY;
}

/**
 * The Friday of the week `date` falls in, weeks running Monday to Sunday.
 *
 * Saturday and Sunday look *forward* to the coming Friday rather than back at
 * yesterday's: somebody opening the sheet at the weekend is thinking about the
 * session ahead of them, not the one that has already happened.
 */
export function fridayOfWeek(date: PlainDate): PlainDate {
  const dow = dayOfWeek(date);
  // Monday=1..Friday=5 are in the same week; Saturday=6 and Sunday=0 roll forward.
  if (dow === 0) return addDays(date, FRIDAY);
  return addDays(date, FRIDAY - dow + (dow === 6 ? 7 : 0));
}

/**
 * The next Friday on or after `date`. With `inclusive: false`, strictly after -
 * which is what "and the one after that" needs when walking the horizon.
 */
export function nextFriday(date: PlainDate, inclusive = true): PlainDate {
  if (inclusive && isFriday(date)) return date;
  const dow = dayOfWeek(date);
  const ahead = (FRIDAY - dow + 7) % 7;
  return addDays(date, ahead === 0 ? 7 : ahead);
}

/**
 * The next `count` Fridays starting from `from`, inclusive if `from` is a Friday.
 *
 * A calendar helper, NOT a schedule. Sessions run fortnightly and are opened by a
 * person, so every Friday this returns is merely a Friday - do not use it to decide
 * that a session exists, which is what the sheet, the worker and the booking route
 * each used to do.
 */
export function upcomingFridays(
  from: PlainDate,
  count: number = DEV_CONNECT_HORIZON_WEEKS,
): PlainDate[] {
  const fridays: PlainDate[] = [];
  let cursor = nextFriday(from);
  for (let i = 0; i < count; i += 1) {
    fridays.push(cursor);
    cursor = addDays(cursor, 7);
  }
  return fridays;
}

/** Minutes past midnight for a wall-clock `HH:MM`. */
function minutesOfDay(startsAtLocal: string): number {
  const match = /^(\d{2}):(\d{2})$/.exec(startsAtLocal);
  if (!match) {
    throw new TypeError(`Expected a HH:MM start time, received ${JSON.stringify(startsAtLocal)}`);
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    throw new TypeError(`Not a valid time of day: ${startsAtLocal}`);
  }
  return hours * 60 + minutes;
}

export function isStartTime(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    minutesOfDay(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * The instant a session begins.
 *
 * Built through `startOfDay`, which resolves the zone offset properly. The tempting
 * one-liner - `new Date(`${heldOn}T09:00:00Z`)` - is wrong by 5h30m and would put a
 * 9am session at 2:30pm IST, taking the booking cutoff with it.
 */
export function sessionStartsAt(
  heldOn: PlainDate,
  startsAtLocal: string = DEV_CONNECT_DEFAULT_START_LOCAL,
  timeZone: string = BUSINESS_TIME_ZONE,
): Date {
  return new Date(startOfDay(heldOn, timeZone).getTime() + minutesOfDay(startsAtLocal) * 60_000);
}

/** The last instant a non-admin may claim a slot. */
export function bookingClosesAt(
  heldOn: PlainDate,
  startsAtLocal: string = DEV_CONNECT_DEFAULT_START_LOCAL,
  timeZone: string = BUSINESS_TIME_ZONE,
): Date {
  return new Date(
    sessionStartsAt(heldOn, startsAtLocal, timeZone).getTime() -
      DEV_CONNECT_BOOKING_CUTOFF_HOURS * 3_600_000,
  );
}

export interface DevConnectSessionTiming {
  heldOn: PlainDate;
  startsAtLocal: string;
  /** Set once somebody records that the session happened. */
  heldAt?: Date | string | null;
}

/**
 * Whether a non-admin may still claim a slot. `now` is injected so the boundary is
 * testable, the same shape as `oneOnOneState`.
 *
 * Presenters need a day to prepare and the line-up has to settle before it is
 * announced, so this closes 24h ahead. It governs *claiming* only: dropping out
 * stays open right up to the session, because somebody who falls ill on Thursday
 * night leaving an empty slot beats them not turning up.
 */
export function isBookingOpen(session: DevConnectSessionTiming, now: Date = new Date()): boolean {
  if (session.heldAt) return false;
  return now.getTime() < bookingClosesAt(session.heldOn, session.startsAtLocal).getTime();
}

/**
 * Session state, derived - never stored.
 *
 * Same reason an assessment stores `status` and derives its stage: a stored state
 * beside the values that produce it is another thing to keep in step, and it would
 * be wrong for as long as the worker was down.
 *
 * Note which event is recorded, versus a 1-on-1. There the record is *not held*,
 * because notes plus acknowledgement already prove a meeting happened. Here it is
 * *held*, because the showcase log is a permanent record of what was presented and
 * a Friday nobody confirmed must not walk into it on the calendar's say-so.
 */
export type DevConnectState = 'UPCOMING' | 'TODAY' | 'AWAITING_RECORD' | 'HELD';

export function devConnectState(
  session: { heldOn: PlainDate; heldAt?: Date | string | null },
  on: PlainDate,
): DevConnectState {
  if (session.heldAt) return 'HELD';
  if (session.heldOn > on) return 'UPCOMING';
  if (session.heldOn === on) return 'TODAY';
  return 'AWAITING_RECORD';
}

/**
 * The sheet, as rows.
 *
 * A slot is an ordinal, not a row in the database: an unclaimed one is the *absence*
 * of a booking. That is what lets a unique index on (session, slotIndex) settle two
 * people clicking the last slot at the same instant, with no pre-check to race.
 */
export interface DevConnectSlot<T extends { slotIndex: number }> {
  slotIndex: number;
  booking: T | null;
}

export function slotsFor<T extends { slotIndex: number }>(
  capacity: number,
  bookings: T[],
): Array<DevConnectSlot<T>> {
  const byIndex = new Map(bookings.map((b) => [b.slotIndex, b]));
  // Bookings past the capacity still show: an admin who shrank the sheet must not
  // make somebody's claim invisible, and the service refuses that shrink anyway.
  const highest = bookings.reduce((max, b) => Math.max(max, b.slotIndex + 1), capacity);
  return Array.from({ length: highest }, (_, slotIndex) => ({
    slotIndex,
    booking: byIndex.get(slotIndex) ?? null,
  }));
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * e.g. "Fri 11 Sep 2026" - the sheet's own heading for a session.
 *
 * The weekday is READ from the date, never assumed. This used to hardcode "Fri",
 * which was true while sessions could only be Fridays and became a lie the moment
 * they could be moved: a session an admin shifted to a Wednesday still announced
 * itself as Friday, on the card, in the booking dialog, in the refusal message and
 * in the mail telling people it had moved.
 */
export function formatSessionDate(heldOn: PlainDate): string {
  return `${WEEKDAYS[dayOfWeek(heldOn)]} ${formatPlainDate(heldOn)}`;
}

/**
 * e.g. "9:00am on Thu 10 Sep 2026" - for the message a late booker is refused with.
 * Naming the deadline answers "when was it, then?" without a trip to Slack.
 */
export function formatBookingDeadline(
  heldOn: PlainDate,
  startsAtLocal: string = DEV_CONNECT_DEFAULT_START_LOCAL,
): string {
  const closesOn = addDays(heldOn, -1);
  const minutes = minutesOfDay(startsAtLocal);
  const hour24 = Math.floor(minutes / 60);
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const mm = String(minutes % 60).padStart(2, '0');
  const suffix = hour24 < 12 ? 'am' : 'pm';
  return `${hour12}:${mm}${suffix} on ${formatSessionDate(closesOn)}`;
}
